import React, {
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useFormik } from "formik";
import { Dropdown } from "primereact/dropdown";
import * as Yup from "yup";
import { uploadDoc } from "../../../Services/Global-Services/GlobalServices";
import { TostMsg } from "../../../App";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { addUpdateEmployeeKeyValue } from "../MyProfile/Service/profileService";
import ImageCropperModal from "../../../Services/Comment/ImageCropperModal";
import UploadFile_KEYS from "../../../Services/Global-Services/UploadFile_Keys";
import ERROR_LIST from "../../../Services/Global-Services/Hadle_Errors";
import ComponentLoader from "../../../Shared/Sidebar/ComponentLoader";

const groupFieldsByCategory = (data) => {
  const grouped = {};

  data.forEach((field) => {
    const groupId = field.OrgEmployeeKeyCategoryDisplayOrder;
    if (!grouped[groupId]) {
      grouped[groupId] = [];
    }
    grouped[groupId].push(field);
  });

  return Object.entries(grouped)
    .sort((a, b) => +a[0] - +b[0])
    .map(([groupId, fields]) => ({
      groupId,
      fields: fields.sort((a, b) => a.KeyDisplayOrder - b.KeyDisplayOrder || 0),
    }));
};

const DynamicEmployeeForm = (params) => {
  const {
    employeeKeyDeta,
    renderForm,
    employeeEditDetails,
    backButtonHideStatus,
    employeeAddId,
  } = params;
  const submitActionRef = useRef("");
  const history = useHistory();
  const [currentGroupIndex, setCurrentGroupIndex] = useState(0);
  const groupedFormData = useMemo(
    () => groupFieldsByCategory(employeeKeyDeta),
    [employeeKeyDeta]
  );

  // Group-based initial values
  const dynamicInitialValues = useMemo(() => {
    const values = {};
    employeeKeyDeta.forEach((field) => {
      values[field.KeyName] = "";
    });
    return values;
  }, [employeeKeyDeta]);

  const getValidationSchemaForGroup = (fields) => {
    if (!Array.isArray(fields) || fields.length === 0) return Yup.object();

    const shape = {};

    fields.forEach((field) => {
      let schema = Yup.mixed();
      if (
        typeof field.DefaultValue === "number" ||
        field.InputType === "dropdown"
      ) {
        schema = Yup.number();
      } else {
        schema = Yup.string();
      }

      if (field.isMandatory === true) {
        schema = schema.required(
          `${field.KeyDesc || field.KeyName} is required`
        );
      }

      shape[field.KeyName] = schema;
    });

    return Yup.object().shape(shape);
  };

  const employeeInfoForm = useFormik({
    initialValues: dynamicInitialValues,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async (data) => {
      setIsImageloading();
      const currentFields = groupedFormData[currentGroupIndex]?.fields || [];
      const currentSchema = getValidationSchemaForGroup(currentFields);
      const currentFormFieldsValue = currentFields.map((field) => {
        const keyName = field.KeyName;
        const keyValue = employeeInfoForm.values[keyName] || "";
        let value = keyValue;
        let text = keyValue;
        if (field.EmpKeyOptions && Array.isArray(field.EmpKeyOptions)) {
          const matchedOption = field.EmpKeyOptions.find(
            (opt) => opt.Id === keyValue
          );
          if (matchedOption) {
            value = matchedOption.Id;
            text = matchedOption.Option;
          } else {
            text = "";
          }
        }
        if (field.InputTypeId === 107) {
          const dateObj = new Date(keyValue);
          if (!keyValue || isNaN(dateObj.getTime())) {
            value = "";
            text = "";
          } else {
            value = dateObj.toISOString().split("T")[0];
            text = value;
          }
        }

        return {
          EmpId: employeeEditDetails[0]?.EmpId ?? employeeAddId?.Id,
          KeyId: field.KeyId,
          Value: value?.toString() || "",
          Text: text,
        };
      });

      setIsLoading(true);
      try {
        await currentSchema.validate(employeeInfoForm.values, {
          abortEarly: false,
        });
        currentFields.forEach((field) =>
          employeeInfoForm.setFieldTouched(field.KeyName, false)
        );

        const changedValues = currentFormFieldsValue.filter((field) => {
          const oldField = employeeEditDetails[0]?.EmpKeyValues?.find(
            (f) => f.KeyId === field.KeyId
          );
          const oldValue = oldField?.Value ?? "";
          return field.Value == "" || field.Value !== oldValue;
        });
        if (changedValues.length > 0) {
          toster.current.success("Record updated successfully");
          let formSaveAndContinue = await addUpdateEmployeeKeyValue(
            changedValues
          );
          if (formSaveAndContinue) {
            toster.current.success("Record updated successfully");
            if (backButtonHideStatus === false) {
              renderForm("profile");
            } else if (currentGroupIndex === groupedFormData.length) {
              history.push(`/Profile/${employeeAddId.GUID}`);
            } else if (employeeEditDetails.length > 0) {
              history.push(`/Profile/${employeeEditDetails[0].GUID}`);
            } else {
              history.push(`/TeamDirectory`);
            }
          }
        } else {
          if (backButtonHideStatus === false) {
            renderForm("profile");
          } else if (currentGroupIndex === groupedFormData.length) {
            history.push(`/Profile/${employeeAddId.GUID}`);
          } else if (employeeEditDetails.length > 0) {
            history.push(`/Profile/${employeeEditDetails[0].GUID}`);
          } else {
            history.push(`/TeamDirectory`);
          }
        }
      } catch (err) {
        setIsLoading(false);
        const errorMap = {};
        if (err.inner) {
          err.inner.forEach((e) => {
            errorMap[e.path] = e.message;
            employeeInfoForm.setFieldTouched(e.path, true);
          });
        }
        employeeInfoForm.setErrors(errorMap);
      }
    },
  });

  const renderFormField = (field) => {
    const error = employeeInfoForm.errors[field.KeyName];
    const touched = employeeInfoForm.touched[field.KeyName];
    const isInvalid = error && touched;
    const employeeId = field.KeyName == "EmploymentId";
    const isDesableField =
      backButtonHideStatus === true ? true : field.SelfUpdate;

    switch (field.InputTypeId) {
      case 101: // Text input
        return (
          <input
            type="text"
            name={field.KeyName}
            onChange={employeeInfoForm.handleChange}
            onBlur={employeeInfoForm.handleBlur}
            value={employeeInfoForm.values[field.KeyName]}
            className={isInvalid ? "form-control is-invalid" : "form-control"}
            disabled={!isDesableField}
          />
        );

      case 102: // Textarea
        return (
          <textarea
            rows="4"
            cols="4"
            name={field.KeyName}
            onChange={employeeInfoForm.handleChange}
            onBlur={employeeInfoForm.handleBlur}
            value={employeeInfoForm.values[field.KeyName]}
            className={
              isInvalid ? "form-control is-invalid" : "form-control h-auto"
            }
            disabled={!isDesableField}
          />
        );

      case 107: // Date input
        return (
          <input
            type="date"
            name={field.KeyName}
            onClick={(e) => e.target.showPicker && e.target.showPicker()}
            onChange={employeeInfoForm.handleChange}
            value={
              employeeInfoForm.values[field.KeyName] &&
                !isNaN(new Date(employeeInfoForm.values[field.KeyName]).getTime())
                ? new Date(employeeInfoForm.values[field.KeyName])
                  .toISOString()
                  .split("T")[0]
                : ""
            }
            className={isInvalid ? "form-control is-invalid" : "form-control"}
            disabled={!isDesableField}
          />
        );

      case 105: // Dropdown
        return (
          <Dropdown
            name={field.KeyName}
            value={employeeInfoForm.values[field.KeyName]}
            onFocus={(e) => e.target.click()}
            onChange={(e) =>
              employeeInfoForm.setFieldValue(field.KeyName, e.value)
            }
            options={field.EmpKeyOptions}
            optionLabel="Option"
            optionValue="Id"
            disabled={!isDesableField}
            filter
          />
        );

      case 106: // Profile image upload
        return (
          <div className="card">
            <div className="card-body">
              <div className="loadresumes">
                <div className="inner">
                  {employeeInfoForm?.values[field.KeyName]?.trim() ? (
                    <img
                      src={employeeInfoForm.values[field.KeyName]}
                      className="editaddimg"
                      onError={handleImageError}
                    />
                  ) : (
                    <img src={defaultImage} className="editaddimg" />
                  )}
                </div>
                <label htmlFor="file-upload" className="custom-file-upload">
                  <i className="fa fa-upload me-2" aria-hidden="true"></i>
                  Upload Profile Image
                </label>
                <input
                  ref={inputFile}
                  type="file"
                  id="file-upload"
                  name={field.KeyName}
                  accept="image/png, image/gif, image/jpeg"
                  style={{ display: "none" }}
                  onChange={(event) => handleFileChange(event, 0)}
                  disabled={!isDesableField}
                />
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  useEffect(() => {
    if (employeeEditDetails && employeeKeyDeta.length > 0) {
      employeeKeyDeta.forEach((field) => {
        const key = field.KeyName;
        let matchedValue;
        if (field.EmpKeyOptions?.length > 0) {
          const rawVal = employeeEditDetails[0]?.EmpKeyValues?.find(
            (kv) => kv.KeyName === key
          )?.Value;
          matchedValue = rawVal !== undefined && rawVal !== "" && rawVal !== null
            ? Number(rawVal)
            : "";
          // matchedValue = Number(
          //   employeeEditDetails[0]?.EmpKeyValues?.find(
          //     (kv) => kv.KeyName === key
          //   )?.Value
          // );
        } else {
          matchedValue = employeeEditDetails[0]?.EmpKeyValues?.find(
            (kv) => kv.KeyName === key
          )?.InferredValue;
        }
        const value = matchedValue ?? employeeEditDetails[key] ?? "";
        employeeInfoForm.setFieldValue(key, value);
        if (key == "ActualDateOfBirth") {
          const date = new Date(matchedValue);
          const yyyy = date.getFullYear();
          const mm = String(date.getMonth() + 1).padStart(2, "0");
          const dd = String(date.getDate()).padStart(2, "0");
          employeeInfoForm.setFieldValue(
            "ActualDateOfBirth",
            `${yyyy}-${mm}-${dd}`
          );
        }

        if (key == "DateOfBirthCeleberatedOn") {
          const date = new Date(matchedValue);
          const yyyy = date.getFullYear();
          const mm = String(date.getMonth() + 1).padStart(2, "0");
          const dd = String(date.getDate()).padStart(2, "0");
          employeeInfoForm.setFieldValue(
            "DateOfBirthCeleberatedOn",
            `${yyyy}-${mm}-${dd}`
          );
        }
      });
    }
  }, [employeeEditDetails, employeeKeyDeta]);

  useEffect(() => {
    const currentFields = groupedFormData[currentGroupIndex]?.fields || [];
    const touchedKeys = Object.keys(employeeInfoForm.touched);
    const anyTouchedInCurrentGroup = currentFields.some((field) =>
      touchedKeys.includes(field.KeyName)
    );

    if (anyTouchedInCurrentGroup) {
      const schema = getValidationSchemaForGroup(currentFields);
      schema
        .validate(employeeInfoForm.values, { abortEarly: false })
        .then(() => {
          // Clear errors
          const cleanErrors = { ...employeeInfoForm.errors };
          currentFields.forEach((field) => {
            delete cleanErrors[field.KeyName];
          });
          employeeInfoForm.setErrors(cleanErrors);
        })
        .catch((err) => {
          const newErrors = {};
          if (err.inner) {
            err.inner.forEach((e) => {
              newErrors[e.path] = e.message;
            });
          }
          employeeInfoForm.setErrors((prev) => ({ ...prev, ...newErrors }));
        });
    }
  }, [employeeInfoForm.values, currentGroupIndex]);

  const handleSaveAndContinue = async () => {
    const currentFields = groupedFormData[currentGroupIndex]?.fields || [];
    const currentSchema = getValidationSchemaForGroup(currentFields);

    const currentFormFieldsValue = currentFields.map((field) => {
      const keyName = field.KeyName;
      const keyValue = employeeInfoForm.values[keyName] || "";
      let value = keyValue;
      let text = keyValue;

      // Handle dropdowns
      if (field.EmpKeyOptions && Array.isArray(field.EmpKeyOptions)) {
        const matchedOption = field.EmpKeyOptions.find(
          (opt) => opt.Id === keyValue
        );
        if (matchedOption) {
          value = matchedOption.Id;
          text = matchedOption.Option;
        } else {
          text = "";
        }
      }

      return {
        EmpId: employeeEditDetails[0]?.EmpId ?? employeeAddId?.Id,
        KeyId: field.KeyId,
        Value: value?.toString() || "",
        Text: text,
      };
    });

    setIsLoading(true);

    try {
      const currentValues = currentFields.reduce((acc, field) => {
        let value = employeeInfoForm.values[field.KeyName];
        if (
          value === undefined ||
          value === null ||
          value === "" ||
          Number.isNaN(value)
        ) {
          value = "";
        }
        acc[field.KeyName] = value;
        return acc;
      }, {});
      await currentSchema.validate(currentValues, { abortEarly: false });
      const touchedReset = {};
      currentFields.forEach((field) => {
        touchedReset[field.KeyName] = false;
      });
      employeeInfoForm.setTouched(touchedReset);
      employeeInfoForm.setErrors({});
      if (currentGroupIndex < groupedFormData.length - 1) {
        setCurrentGroupIndex((prev) => prev + 1);
        const changedValues = currentFormFieldsValue.filter((field) => {
          const oldField = employeeEditDetails[0]?.EmpKeyValues?.find(
            (f) => f.KeyId === field.KeyId
          );
          const oldValue = oldField?.Value ?? "";
          return field.Value !== oldValue;
        });
        // const changedValues = currentFormFieldsValue.filter((field) => {
        //   const oldField = employeeEditDetails[0]?.EmpKeyValues?.find(
        //     (f) => f.KeyId === field.KeyId
        //   );
        //   const oldValue = oldField?.Value ?? "";

        //   return field.Value !== "" && field.Value !== oldValue;
        // });

        if (changedValues.length > 0) {
          let formSaveAndContinue = await addUpdateEmployeeKeyValue(
            changedValues
          );
          if (formSaveAndContinue) {
            toster.current.success("Record updated successfully");
          }
        }
      } else {
        employeeInfoForm.handleSubmit();
      }

      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);

      const errorMap = {};
      if (err.inner) {
        err.inner.forEach((e) => {
          errorMap[e.path] = e.message;
          employeeInfoForm.setFieldTouched(e.path, true);
        });
      }
      employeeInfoForm.setErrors(errorMap);
      return;
    }
  };

  const currentGroup = groupedFormData[currentGroupIndex];
  const [file, setFile] = useState(null);
  const inputFile = useRef(null);
  const defaultImage = "/images/placeholder_profile.png";
  const [isImageloading, setIsImageloading] = useState(false);
  const toster = useContext(TostMsg);
  const [acceptRatio, setAcceptRatio] = useState(0);
  const [showCropper, setShowCropper] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleFileChange = async (event, type) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setImageSrc(reader.result);
        setShowCropper(true);
        setAcceptRatio(1 / 1);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCropComplete = (croppedBlob) => {
    uploadDocFile([
      new File([croppedBlob], "cropped.jpg", { type: "image/jpeg" }),
    ]);
  };

  const handleImageError = useCallback((event) => {
    event.target.src = defaultImage;
  }, []);

  async function uploadDocFile(file) {
    setIsImageloading(true);
    if (file) {
      const formData = new FormData();
      for (let i = 0; i < file.length; i++) {
        formData.append("files", file[i]);
      }
      setIsLoading(true);
      let params = {
        UploadType: UploadFile_KEYS?.EmployeeProfile,
        RefId: employeeEditDetails[0]?.EmpId ?? employeeAddId?.Id ?? 0,
      };
      try {
        const response = await uploadDoc(params, formData);
        const data = await response;
        const ImageURL = data?.Data;
        if (ImageURL) {
          const imageValue = ImageURL.join(", ");
          employeeInfoForm.setFieldValue("ProfileImage", imageValue);
        }
        setIsImageloading(false);
        setIsLoading(false);
      } catch (error) {
        setIsImageloading(false);
        setIsLoading(false);
        toster.current.error(ERROR_LIST.DefaultError);
      } finally {
      }
    } else {
      setIsImageloading(false);
      toster.current.warn("No file selected");
    }
  }

  const handleSubmitWithAction = (action) => {
    submitActionRef.current = action;

    requestAnimationFrame(() => {
      employeeInfoForm.handleSubmit();
    });
  };

  return (
    <form onSubmit={employeeInfoForm.handleSubmit}>
      {backButtonHideStatus && (
        <span className="tagtitle">
          {console.log(currentGroup?.fields[0])}
          {console.log(currentGroup?.fields[0].OrgEmployeeKeyCategoryDescription)}
          {currentGroup?.fields[0].OrgEmployeeKeyCategoryDescription}
        </span>
      )}
      <div
        className={
          backButtonHideStatus === false ? "row  mt-4 gx-3 gy-2" : "row  g-3"
        }
      >
        {currentGroup?.fields?.map((field, idx) => (
          <div className="col-md-6" key={idx}>
            <div className="innerform-grp">
              <label className="form-label">
                <div className="basic-infotop-icon">
                  <span>
                    {console.log(field.KeyDesc, "field.KeyDesc")}
                    {field.KeyDesc}
                    {field.isMandatory && <i className="inValidError">*</i>}
                  </span>
                  {field.HelpText && (
                    <div class="ms-2 info-tooltip-area ">
                      <div className="pf-img-icon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          fill="currentColor"
                          class="bi bi-info"
                          viewBox="0 0 16 16"
                        >
                          <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0" />
                        </svg>
                      </div>
                      <div class="info-tooltip-content info-area">
                        <div class="purchase-tooltip-content">
                          {field.HelpText}
                        </div>
                      </div>
                    </div>
                    // <img
                    //   src="/images/org-img/leave/info.svg"
                    //   className="info"
                    //   data-toggle="tooltip"
                    //   data-placement="right"
                    //   title={field.HelpText}
                    // />
                  )}
                </div>
              </label>
              {showCropper && imageSrc && (
                <ImageCropperModal
                  image={imageSrc}
                  onCropComplete={handleCropComplete}
                  onClose={() => setShowCropper(false)}
                  acceptRatio={acceptRatio}
                />
              )}
              {renderFormField(field)}
              {employeeInfoForm.errors[field.KeyName] &&
                employeeInfoForm.touched[field.KeyName] && (
                  <div className="invalid-feedback">
                    {employeeInfoForm.errors[field.KeyName]}
                  </div>
                )}
            </div>
          </div>
        ))}
      </div>

      <div className="col-md-12">
        <div className="btns-grp text-right mt-3">
          {backButtonHideStatus && (
            <button
              type="button"
              onClick={() => {
                if (currentGroupIndex > 0) {
                  setCurrentGroupIndex((prev) => prev - 1);
                } else {
                  renderForm("BasicInfo");
                }
              }}
              className="backbutton"
            >
              Back
            </button>
          )}
          {currentGroupIndex < groupedFormData.length && (
            <button
              type="button"
              className="savebtn-click"
              onClick={() => handleSubmitWithAction("exit")}
            >
              Save & exit
            </button>
          )}
          {currentGroupIndex < groupedFormData.length - 1 && (
            <button
              type="button"
              onClick={() => handleSaveAndContinue()}
              className="publish-click"
            >
              continue
            </button>
          )}
        </div>
        {isLoading ? (
          <Suspense>
            <ComponentLoader />
          </Suspense>
        ) : (
          ""
        )}
      </div>
    </form>
  );
};

export default DynamicEmployeeForm;
